import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import 'features/reports/pages/my-reports/my-reports.scss';
import { DataTable, Icon } from '@ampol/design-system';
import ContentControls from 'components/content-controls/content-controls';
import FilterControl from 'components/filter-control/filter-control';

import PageHeader from 'components/layout/page-header/page-header';
import Validation from 'constants/validation-messages';
import { useCheckGetAllAccountsRequest } from 'components/use-get-all-accounts-request/use-get-all-accounts-request';
import { trackCustomEvent } from 'utilities/app-insights';
import { UserState } from 'types/UserState';
import { formatDate } from 'utilities/dayjs-utilities';
import { DATE_FORMAT } from 'utilities/constants';
import { getReportsList, getReportsListReset, downloadReport } from 'actions/reportsActions';

/**
 * My Reports page listing reports on a Tab Screen
 * @returns JSX Element
 */
const ViewReports = () => {
  const dispatch = useDispatch();
  useCheckGetAllAccountsRequest();

  const userStore: UserState = useSelector((state: any) => state.userStore);
  const loggedInUser = userStore.loggedInUser;
  const assignedGrandfatherClientNumber = userStore.assignedGrandfatherClientNumber;
  const assignedAccountNumber = userStore.assignedAccountNumber;
  const allAccounts = useSelector((state: any) => state.accountsStore.allAccounts);

  const reportsList = useSelector((state: any) => state.reportsStore.reportsList);
  const reportsListError = useSelector((state: any) => state.reportsStore.reportsListError);
  const reportsListResultSize = useSelector((state: any) => state.reportsStore.reportsListResultSize);
  const reportsListLoading = useSelector((state: any) => state.reportsStore.reportsListLoading);

  const [isAccountSelected, setIsAccountSelected] = useState(false);
  const [selectedContractNumber, setSelectedContractNumber] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('publishedDate');
  const [sortDirection, setSortDirection] = useState('desc');

  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 10;
  const offset = (pageNumber - 1) * pageSize;

  useEffect(() => {
    if (assignedAccountNumber) {
      resetSchedules();
    }
  }, [assignedAccountNumber]);

  const resetSchedules = () => {
    dispatch(getReportsListReset());
    setSelectedContractNumber(null);
    setIsAccountSelected(false);
    setPageNumber(1);
    setSearchTerm('');
    setSortColumn('publishedDate');
    setSortDirection('desc');
  };

  useEffect(() => {
    if (isAccountSelected) {
      dispatch(
        getReportsList(
          assignedGrandfatherClientNumber,
          selectedContractNumber,
          pageSize,
          offset,
          sortColumn,
          sortDirection,
          searchTerm
        )
      );
    }
  }, [pageNumber, searchTerm, sortColumn, sortDirection, isAccountSelected]);

  useEffect(() => {
    if (allAccounts?.length === 1) {
      setIsAccountSelected(true);
      setSelectedContractNumber(allAccounts[0].contractNumber);
    }
  }, [allAccounts]);

  const handleSelectAccount = (accountNumber) => {
    setPageNumber(1);

    if (accountNumber) {
      trackCustomEvent(
        'Select Account Dropdown',
        loggedInUser,
        assignedAccountNumber,
        'Selected an account to view list of related Reports.',
        { selectedAccount: accountNumber }
      );
      const account = allAccounts.find((x) => x.contractNumber === accountNumber);
      setIsAccountSelected(true);
      setSelectedContractNumber(account.contractNumber);
    } else {
      setIsAccountSelected(false);
      setSelectedContractNumber(null);
      dispatch(getReportsListReset());
    }
  };

  const onTableColumnSorted = (column, isAsc) => {
    setSortColumn(column);
    setSortDirection(isAsc ? 'asc' : 'desc');
  };

  const handleSearch = (input) => {
    window.dataLayer.push({
      event: 'Card_Portal_Reports_Search',
      search_input: input,
    });
    trackCustomEvent(
      'Search Reports List',
      loggedInUser,
      assignedAccountNumber,
      'Searched for specific reports matching name, date or format.'
    );
    setSearchTerm(input);
  };

  const onDownloadReportClick = (documentId, reportName, contentType, publishedDate) => {
    window.dataLayer.push({
      event: 'Card_Portal_Reports_Report_Download',
      button_click: true,
      format: contentType,
      report_name: reportName,
      published_date: publishedDate,
    });
    trackCustomEvent('Click Download', loggedInUser, assignedAccountNumber, 'Clicked to download a report.');
    dispatch(
      downloadReport(assignedGrandfatherClientNumber, selectedContractNumber, documentId, reportName, contentType)
    );
  };

  const handlePageChange = (value) => {
    window.dataLayer.push({
      event: 'Card_Portal_Reports_Page_Change',
      page_selected: value,
    });
    setPageNumber(() => value);

    trackCustomEvent(
      'Change Page Number',
      loggedInUser,
      assignedAccountNumber,
      'Clicked to change page of reports data.'
    );
  };

  return (
    <>
      <PageHeader
        showAccountSelector={true}
        accounts={allAccounts}
        isAccountsFlatList={true}
        onAccountSelected={handleSelectAccount}
        selectedAccountNumber={selectedContractNumber}
        showButton={false}
      />
      <FilterControl
        showFilterControl={false}
        showSearch={true}
        defaultSearchValue={searchTerm}
        handleSearch={handleSearch}
      />
      <ContentControls
        showPageControls={true}
        showSearch={false}
        handleSearch={handleSearch}
        selectControl={false}
        pageNumber={pageNumber}
        totalRecords={reportsListResultSize}
        itemCount={reportsListResultSize}
        onPageChange={handlePageChange}
      />
      <div className='view-reports__reports-list'>
        <DataTable
          caption={'Completed Reports'}
          columns={[
            {
              header: 'Report Name',
              accessorKey: 'reportName',
            },
            {
              header: 'Date',
              id: 'publishedDate',
              accessorFn: (x) => formatDate(x.publishedDate, DATE_FORMAT),
              defaultSorted: 'desc',
            },
            {
              header: 'Format',
              accessorKey: 'contentType',
              disableSort: true,
            },
            {
              id: 'action',
              accessorFn: (x) => (
                <button
                  className='view-reports__download'
                  onClick={() => {
                    onDownloadReportClick(x.documentId, x.reportName, x.contentType, x.publishedDate);
                  }}
                >
                  <Icon url={'/icons/download.svg'} />
                </button>
              ),
              rtl: true,
            },
          ]}
          columnTemplate='5fr 1fr 1fr 75px'
          onColumnSorted={onTableColumnSorted}
          data={reportsList}
          tableVariant='list'
          isLoading={reportsListLoading}
          noDataMessage={reportsListError ? Validation.CONTENT_ERROR : Validation.NO_CONTENT}
        />
      </div>
    </>
  );
};

export default ViewReports;
